import { z } from 'zod';

// ---------------------------------------------------------------------------
// customer_review_photo.upload.requested — emitted by admin after staging the
// raw browser file in Storage (bucket `wandero`,
// `staging/customer-reviews/{uuid}.{ext}`).
//
// The worker handler (T6a) reuses the SAME sanitizeOriginal() step
// (privacy-mandatory strip of EXIF/GPS/ICC — UGC is customer-supplied) before
// writing the final object. The FINAL storage_path is built SERVER-SIDE from
// the numeric review_id (reviews/<review_id>/<idx>.webp) — never from the
// client-supplied filename (path-traversal guard). Structural sibling of
// realization.upload.requested, but attaches to `customer_reviews` (1—N
// photos) instead of a wallpaper.
// ---------------------------------------------------------------------------

export const customerReviewPhotoUploadRequestedSchema = z.object({
  /** Parent review the photo attaches to (customer_reviews.id). */
  review_id: z.number().int().positive(),
  /** Staging key in the `wandero` bucket, e.g. 'staging/customer-reviews/<uuid>.jpg'. */
  storage_path: z.string().min(1),
  original_filename: z.string().min(1),
  content_type: z.string().min(1),
  size_bytes: z.number().int().positive(),
  /** Desired ordering within the review's photo set (server may re-derive). */
  sort_order: z.number().int().min(0).optional(),
});
export type CustomerReviewPhotoUploadRequestedPayload = z.infer<
  typeof customerReviewPhotoUploadRequestedSchema
>;

// ---------------------------------------------------------------------------
// customer_review_photo.upload.processed — emitted by the worker handler on
// success or error.
// ---------------------------------------------------------------------------

export const customerReviewPhotoUploadProcessedSuccessSchema = z.object({
  photo_id: z.number().int().positive(),
  review_id: z.number().int().positive(),
  /** Final server-built WebP path: reviews/<review_id>/<idx>.webp */
  storage_path: z.string(),
});

export const customerReviewPhotoUploadProcessedErrorSchema = z.object({
  error: z.string(),
  storage_path: z.string(),
});

export const customerReviewPhotoUploadProcessedSchema = z.union([
  customerReviewPhotoUploadProcessedSuccessSchema,
  customerReviewPhotoUploadProcessedErrorSchema,
]);
export type CustomerReviewPhotoUploadProcessedPayload = z.infer<
  typeof customerReviewPhotoUploadProcessedSchema
>;

// ---------------------------------------------------------------------------
// FORWARD-COMPAT (deferred T6b / spec §Scope decision — fast-follow, code-only)
//
// The per-photo upscale flow reuses the existing fal upscale pipeline
// (kind='upscale' models, mirror ai.interior.upscale). Payload schemas are
// defined NOW so the fast-follow adds the worker handler + EVENT registration
// only — no schema churn, no 2nd migration (the upscale_* columns already ship
// in migration 20260712000001). REPLACE semantics: the upscaled image
// overwrites customer_review_photos.storage_path.
// ---------------------------------------------------------------------------

export const customerReviewPhotoUpscaleRequestedSchema = z.object({
  /** Photo to upscale (customer_review_photos.id). */
  photo_id: z.number().int().positive(),
  /** kind='upscale' ai_models.id. */
  model_id: z.number().int().positive(),
  /** Requested scale (slider-mode models only; ignored for fixed/none). */
  scale: z.number().positive().optional(),
});
export type CustomerReviewPhotoUpscaleRequestedPayload = z.infer<
  typeof customerReviewPhotoUpscaleRequestedSchema
>;

export const customerReviewPhotoUpscaleProcessedSuccessSchema = z.object({
  photo_id: z.number().int().positive(),
  /** New storage_path after REPLACE. */
  storage_path: z.string(),
  cost_usd: z.number().nonnegative().nullable(),
});

export const customerReviewPhotoUpscaleProcessedErrorSchema = z.object({
  photo_id: z.number().int().positive(),
  error: z.string(),
});

export const customerReviewPhotoUpscaleProcessedSchema = z.union([
  customerReviewPhotoUpscaleProcessedSuccessSchema,
  customerReviewPhotoUpscaleProcessedErrorSchema,
]);
export type CustomerReviewPhotoUpscaleProcessedPayload = z.infer<
  typeof customerReviewPhotoUpscaleProcessedSchema
>;
